<template>
  <div class="borrowerAttribute" :data-cy-id="question.id" :data-cy-alias="question.alias" :class="{attributeClass, 'inline': formLayout === 'inline'}">
    <div>
      <component
        v-bind:hideHelpText='hideHelpText'
        :is="componentType"
        @answer="onAnswer(question, $event)"
        @invalidity="$emit('invalidity', $event)"
        @legaltext-checked="$emit('legaltext-checked')"
        :fourDigitSSN="fourDigitSSN"
        :customButton="customButton"
        :question="question"
        :borrowerInterest="borrowerInterest"
        :displayAltOptions="onBasicInfo"
        :displayMonthNum="formLayout"
        :disabled="disabled || question.disabled"
        :key="classQuestionId"
        :businessType="businessType"
        :averageMonthlyPayroll="averageMonthlyPayroll"
        :portalAction="portalAction"
        :errorState="errorState"
      ></component>
    </div>
  </div>
</template>

<script>
import Address from './BorrowerAttributeAddress.vue'
import Boolean from './BorrowerAttributeBoolean.vue'
import LongBoolean from './BorrowerAttributeLongBoolean.vue'
import BorrowerCurrency from './BorrowerAttributeCurrency.vue'
import Email from './BorrowerAttributeEmail.vue'
import Number from './BorrowerAttributeNumber.vue'
import Percent from './BorrowerAttributePercent.vue'
import Select from './BorrowerAttributeSelect.vue'
import TaxId from './BorrowerAttributeTaxId.vue'
import Text from './BorrowerAttributeText.vue'
import TextArea from './BorrowerAttributeTextArea.vue'
import Ssn from './BorrowerAttributeSsn.vue'
import States from './BorrowerAttributeStates.vue'
import Naics from './BorrowerAttributeNaics.vue'
// Using BADate to avoid potential conflicts with js Date object...
// ... not to remind you of that bad date.
import BADate from './BorrowerAttributeDate.vue'
import DateInput from './BorrowerAttributeDateInput.vue'
import DateMonthYear from './BorrowerAttributeDateMonthYear.vue'
import MultiSelect from './BorrowerAttributeMultiSelect.vue'
import MultiSelectCheckbox from './BorrowerAttributeMultiSelectCheckbox.vue'
import WidgetBoxSelect from './WidgetBoxSelect.vue'
import WidgetMultiBoxSelect from './WidgetMultiBoxSelect.vue'
import WidgetBooleanCheckbox from './WidgetBooleanCheckbox.vue'

export default {
  props: {
    customButton: Object,
    onBasicInfo: Boolean,
    updateIndex: Number,
    disabled: Boolean,
    question: Object,
    naicsCode: Number,
    borrowerInterest: String,
    v: Object,
    formLayout: String,
    businessType: String,
    averageMonthlyPayroll: String,
    portalAction: Boolean,
    fourDigitSSN: Boolean,
    errorState: Boolean,
    hideHelpText: Boolean,
  },
  emits: ['invalidity','answer', 'legaltext-checked'],
  computed: {
    classQuestionId () {
      return this.question ? String(this.question.alias).replace(/\.|\_/g, '-') : ''
    },
    attributeClass() {
      return 'borrower-attribute ba-' + this.classQuestionId + '-container'
    },
    componentType() {
      let currentComponent = 'ba-text'
      switch (this.question.type) {
        case 'address':
          currentComponent = 'ba-address'
          break
        case 'bool':
          currentComponent = 'ba-boolean'
          break
        case 'longBool':
          currentComponent = 'ba-longBoolean'
          break
        case 'currency':
          currentComponent = 'ba-currency'
          break
        case 'decimal':
          currentComponent = 'ba-number'
          break
        case 'email':
          currentComponent = 'ba-email'
          break
        case 'date':
          currentComponent = 'ba-date'
          break
        case 'date-single':
        case 'date-input':
          currentComponent = 'ba-dateInput'
          break
        case 'date-month-year':
          currentComponent = 'ba-dateMonthYear'
          break
        case 'multiselect':
          currentComponent = 'ba-multiSelect'
          break
        case 'number':
          currentComponent = 'ba-number'
          // If number is percent render as input group with appended %.
          // Hacked here to avoid breaking other systems (e.g. pipeline).
          if (this.question.alias.includes('percent')) {
            currentComponent = 'ba-percent'
          }
          break
        case 'percent':
          currentComponent = 'ba-percent'
          break
        case 'select':
          currentComponent = 'ba-select'
          break
        case 'ssn':
          currentComponent = 'ba-ssn'
          break
        case 'taxId':
          currentComponent = 'ba-taxId'
          break
        case 'text':
          currentComponent = 'ba-text'
          break
        case 'textarea':
          currentComponent = 'ba-textarea'
          break
        case 'tel':
          currentComponent = 'ba-number'
          break
        case 'states':
          currentComponent = 'ba-states'
          break
        case 'naics':
          currentComponent = 'ba-naics'
          break
        case 'multiSelectCheckbox':
          currentComponent = 'ba-multiSelectCheckbox'
          break
        case 'boxSelect':
          currentComponent = 'widgetBoxSelect'
          break
        case 'multiBoxSelect':
          currentComponent = 'widgetMultiBoxSelect'
          break
        case 'boolCheckbox':
          currentComponent = 'widgetBooleanCheckbox'
          break
      }
      return currentComponent
    },
  },
  methods: {
    onAnswer (question, { alias = '', value, isInvalid, submitImmediately = false, isTrusted}) {
      let attr = {}
      if (value instanceof Object && value.hasOwnProperty('alias') && value.hasOwnProperty('value')) {
        attr = value
      } else if (alias) {
        attr = {alias, value}
      } else if (question.components && question.components.length == 1) {
        attr = {
          alias: question.components[0].alias || question.alias,
          value
        }
      } else {
        attr = {
          alias: question.alias,
          value
        }
      }
      this.$emit('answer', { attr, isInvalid, submitImmediately, isTrusted })
    }
  },
  components: {
    'ba-address': Address,
    'ba-boolean': Boolean,
    'ba-longBoolean': LongBoolean,
    'ba-currency': BorrowerCurrency,
    'ba-email': Email,
    'ba-number': Number,
    'ba-percent': Percent,
    'ba-select': Select,
    'ba-ssn': Ssn,
    'ba-taxId': TaxId,
    'ba-text': Text,
    'ba-textarea': TextArea,
    'ba-date': BADate,
    'ba-dateInput': DateInput,
    'ba-dateMonthYear': DateMonthYear,
    'ba-multiSelect': MultiSelect,
    'ba-multiSelectCheckbox': MultiSelectCheckbox,
    'ba-states': States,
    'ba-naics': Naics,
    'widgetBoxSelect': WidgetBoxSelect,
    'widgetMultiBoxSelect': WidgetMultiBoxSelect,
    'widgetBooleanCheckbox': WidgetBooleanCheckbox,
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  .borrowerAttribute {
    margin-bottom: 24px;
    width: 100%;
  }

  .inline {
    .form-group {
      display: flex;
      margin-bottom: 0;
      > div {
        width: 48%;
        padding-right: 2%;
      }
      @include media-breakpoint-down(md) {
        flex-direction: column;
        > div {
          width: 100%;
        }
      }
    }
  }

</style>
